import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://kaxlblujljjnghknrdvb.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtheGxibHVqbGpqbmdoa25yZHZiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk4NTM3NTMsImV4cCI6MjA3NTQyOTc1M30.HbVQR49zzKLom2fONnoTvCNo27UDVGv8XPID7CJOx0Q';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Types
export interface Category {
  id: string;
  name: string;
  color: string;
  section: 'unlimited' | 'robotics' | 'sex-machines';
  created_at?: string;
}

export interface Product {
  id: string;
  name_cs: string;
  name_en?: string;
  name_de?: string;
  name_sk?: string;
  description_cs?: string;
  description_en?: string;
  description_de?: string;
  description_sk?: string;
  price: number;
  currency: string;
  category: 'unlimited' | 'robotics' | 'sex-machines';
  image: string;
  images?: string;
  manual?: string;
  video_tutorial?: string;
  in_stock: boolean;
  featured: boolean;
  discount?: number;
  is_on_sale?: boolean;
  is_sold_out?: boolean;
  is_recommended?: boolean;
  is_rental?: boolean;
  rental_price_monthly?: number;
  rental_insurance_monthly?: number;
  rental_hourly_rate?: number;
  vat_included?: boolean;
  order_index?: number;
  created_at?: string;
  updated_at?: string;
  categories?: Category[];
}

export interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  billing_address: string;
  shipping_address?: string;
  gate_width?: string;
  gate_height?: string;
  work_hours?: string;
  notes?: string;
  shipping_method: string;
  subtotal: number;
  shipping_cost: number;
  total: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  created_at?: string;
  items?: OrderItem[];
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  price: number;
  created_at?: string;
  product?: Product;
}

export interface News {
  id: string;
  title_cs: string;
  title_en: string;
  title_de: string;
  title_sk: string;
  excerpt_cs: string;
  excerpt_en: string;
  excerpt_de: string;
  excerpt_sk: string;
  content_cs: string;
  content_en: string;
  content_de: string;
  content_sk: string;
  date: string;
  image: string;
  category: string;
  created_at?: string;
  updated_at?: string;
}

export interface ProjectReference {
  id: string;
  title_cz: string;
  title_en: string;
  title_de: string;
  title_sk: string;
  description_cz: string;
  description_en: string;
  description_de: string;
  description_sk: string;
  client_name: string;
  project_date: string;
  image_url: string;
  images?: string[];
  category: string;
  order_index: number;
  created_at?: string;
  updated_at?: string;
}

// Product and Category caching
const productCache = new Map<string, { data: any[], timestamp: number }>();
const categoryCache = new Map<string, { data: any[], timestamp: number }>();
const CACHE_DURATION = 300000; // 5 minutes

// Cache invalidation functions
export const clearProductCache = () => {
  productCache.clear();
};

export const clearCategoryCache = () => {
  categoryCache.clear();
};

// Lightweight product fetch for cart — only fields needed for display
const cartProductCache: { data: any[] | null; timestamp: number } = { data: null, timestamp: 0 };

export const getProductsForCart = async () => {
  if (cartProductCache.data && Date.now() - cartProductCache.timestamp < CACHE_DURATION) {
    return cartProductCache.data;
  }

  const { data, error } = await supabase
    .from('products')
    .select('id, name_cs, name_en, name_de, name_sk, price, currency, category, image, discount, in_stock, is_sold_out, order_index, vat_included')
    .order('order_index', { ascending: true });

  if (error) {
    if (cartProductCache.data) return cartProductCache.data;
    throw new Error(`Failed to fetch products: ${error.message}`);
  }

  cartProductCache.data = data || [];
  cartProductCache.timestamp = Date.now();
  return cartProductCache.data;
};

export const clearCartProductCache = () => {
  cartProductCache.data = null;
  cartProductCache.timestamp = 0;
};


// Lightweight product fetch — excludes heavy `manual` and `images` columns
// that can be multi-MB base64 blobs and cause statement timeouts.
const PRODUCT_LIGHT_COLUMNS = 'id, name_cs, name_en, name_de, name_sk, description_cs, description_en, description_de, description_sk, price, currency, category, image, images, in_stock, featured, discount, is_on_sale, is_sold_out, is_recommended, is_rental, rental_price_monthly, rental_insurance_monthly, rental_hourly_rate, vat_included, video_tutorial, order_index, created_at, updated_at';

export const getProductsLight = async (section?: string, includeCategories: boolean = true, isRental?: boolean) => {
  try {
    const cacheKey = `light-${section || 'all'}-${includeCategories}-${isRental ?? 'any'}`;
    const cached = productCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    let query = supabase
      .from('products')
      .select(PRODUCT_LIGHT_COLUMNS)
      .order('order_index', { ascending: true })
      .limit(100);

    if (section) {
      query = query.eq('category', section);
    }

    if (isRental !== undefined) {
      query = query.eq('is_rental', isRental);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching products (light):', error);
      if (cached) return cached.data;
      throw new Error(`Failed to fetch products: ${error.message}`);
    }

    if (!data || data.length === 0) {
      const result: any[] = [];
      productCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    }

    if (includeCategories) {
      const productIds = data.map(p => p.id);
      const { data: productCategories } = await supabase
        .from('product_categories')
        .select('product_id, category_id')
        .in('product_id', productIds);

      const categoryMap = new Map<string, string[]>();
      (productCategories || []).forEach((pc: any) => {
        const existing = categoryMap.get(pc.product_id) || [];
        categoryMap.set(pc.product_id, [...existing, pc.category_id]);
      });

      const result = data.map(product => ({
        ...product,
        manual: '',
        category_ids: categoryMap.get(product.id) || []
      }));

      productCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    }

    const result = data.map(product => ({
      ...product,
      manual: '',
      category_ids: []
    }));

    productCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (err: any) {
    console.error('Exception fetching products (light):', err);
    throw new Error(`Failed to fetch products: ${err.message || 'Unknown error'}`);
  }
};

export const getProducts = async (section?: string, includeCategories: boolean = true) => {
  try {
    const cacheKey = `${section || 'all'}-${includeCategories}`;
    const cached = productCache.get(cacheKey);

    // Return cached data if it's still fresh
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    let query = supabase
      .from('products')
      .select('*')
      .order('order_index', { ascending: true })
      .limit(100);

    if (section) {
      query = query.eq('category', section);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching products:', error);

      // Return cached data if available, even if expired
      if (cached) {
        return cached.data;
      }
      throw new Error(`Failed to fetch products: ${error.message}`);
    }

    if (!data || data.length === 0) {
      const result: any[] = [];
      productCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    }

    // If categories are needed, fetch them in one query
    if (includeCategories) {
      const productIds = data.map(p => p.id);

      const { data: productCategories } = await supabase
        .from('product_categories')
        .select('product_id, category_id')
        .in('product_id', productIds);

      const categoryMap = new Map<string, string[]>();
      (productCategories || []).forEach((pc: any) => {
        const existing = categoryMap.get(pc.product_id) || [];
        categoryMap.set(pc.product_id, [...existing, pc.category_id]);
      });

      const result = data.map(product => ({
        ...product,
        category_ids: categoryMap.get(product.id) || []
      }));

      productCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    }

    const result = data.map(product => ({
      ...product,
      category_ids: []
    }));

    productCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (err: any) {
    console.error('Exception fetching products:', err);
    throw new Error(`Failed to fetch products: ${err.message || 'Unknown error'}`);
  }
};

export const createProduct = async (productData: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('products')
    .insert([productData])
    .select()
    .single();

  if (error) {
    console.error('Error creating product:', error);
    throw error;
  }

  return data;
};

export const updateProduct = async (id: string, productData: Partial<Product>) => {
  const { data, error } = await supabase
    .from('products')
    .update({ ...productData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('id, name_cs, name_en, name_de, name_sk, price, category, image, is_rental, rental_price_monthly, rental_insurance_monthly, rental_hourly_rate, vat_included, order_index')
    .single();

  if (error) {
    console.error('Error updating product:', error);
    throw error;
  }

  return data;
};

export const deleteProduct = async (id: string) => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// Category functions with caching
export const getCategories = async (section?: string) => {
  try {
    const cacheKey = section || 'all';
    const cached = categoryCache.get(cacheKey);

    // Return cached data if it's still fresh
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    let query = supabase
      .from('categories')
      .select('*')
      .order('name', { ascending: true });

    if (section) {
      query = query.eq('section', section);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching categories:', error);

      // Return cached data if available
      if (cached) {
        return cached.data;
      }
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    const result = data || [];
    categoryCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (err: any) {
    console.error('Exception fetching categories:', err);
    throw new Error(`Failed to fetch categories: ${err.message || 'Unknown error'}`);
  }
};

export const createCategory = async (categoryData: Omit<Category, 'id' | 'created_at'>) => {
  console.log('createCategory called with:', categoryData);

  const { data, error } = await supabase
    .from('categories')
    .insert([categoryData])
    .select()
    .single();

  if (error) {
    console.error('Error creating category:', error);
    console.error('Error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    throw new Error(`Nepodařilo se vytvořit kategorii: ${error.message}`);
  }

  console.log('Category created successfully:', data);
  return data;
};

export const updateCategory = async (id: string, categoryData: Partial<Category>) => {
  const { data, error } = await supabase
    .from('categories')
    .update(categoryData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating category:', error);
    throw error;
  }

  return data;
};

export const deleteCategory = async (id: string) => {
  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// Product-Category relationship functions
export const addProductToCategory = async (productId: string, categoryId: string) => {
  const { error } = await supabase
    .from('product_categories')
    .insert([{ product_id: productId, category_id: categoryId }]);

  if (error) {
    console.error('Error adding product to category:', error);
    throw error;
  }
};

export const removeProductFromCategory = async (productId: string, categoryId: string) => {
  const { error } = await supabase
    .from('product_categories')
    .delete()
    .eq('product_id', productId)
    .eq('category_id', categoryId);

  if (error) {
    console.error('Error removing product from category:', error);
    throw error;
  }
};

export const updateProductCategories = async (productId: string, categoryIds: string[]) => {
  // First remove all existing categories
  await supabase
    .from('product_categories')
    .delete()
    .eq('product_id', productId);

  // Then add new categories
  if (categoryIds.length > 0) {
    const { error } = await supabase
      .from('product_categories')
      .insert(categoryIds.map(categoryId => ({ product_id: productId, category_id: categoryId })));

    if (error) {
      console.error('Error updating product categories:', error);
      throw error;
    }
  }
};

// Order functions
export const createOrder = async (orderData: Omit<Order, 'id' | 'created_at'>, items: Omit<OrderItem, 'id' | 'order_id' | 'created_at'>[]) => {
  // Create order
  const { data: order, error: orderError } = await supabase
    .from('orders')
    .insert([orderData])
    .select()
    .single();

  if (orderError) {
    console.error('Error creating order:', orderError);
    throw orderError;
  }

  // Create order items
  const orderItems = items.map(item => ({
    ...item,
    order_id: order.id
  }));

  const { error: itemsError } = await supabase
    .from('order_items')
    .insert(orderItems);

  if (itemsError) {
    console.error('Error creating order items:', itemsError);
    throw itemsError;
  }

  return order;
};

export const getOrders = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      items:order_items(
        *,
        product:products(*)
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching orders:', error);
    return [];
  }

  return data || [];
};

// Utility function to convert legacy product format to Supabase format
export const convertLegacyProduct = (legacyProduct: any): Omit<Product, 'id' | 'created_at' | 'updated_at'> => {
  const getName = (field: any) => {
    if (typeof field === 'object' && field !== null) {
      return field;
    }
    return typeof field === 'string' ? field : '';
  };

  const getDescription = (field: any) => {
    if (typeof field === 'object' && field !== null) {
      return field;
    }
    return typeof field === 'string' ? field : '';
  };

  const name = getName(legacyProduct.name);
  const description = getDescription(legacyProduct.description);

  // Ensure name_cs is never empty (it's a required field)
  const name_cs = typeof name === 'object' ? (name.cs || 'Nový produkt') : (name || 'Nový produkt');

  return {
    name_cs: name_cs,
    name_en: typeof name === 'object' ? (name.en || '') : '',
    name_de: typeof name === 'object' ? (name.de || '') : '',
    name_sk: typeof name === 'object' ? (name.sk || '') : '',
    description_cs: typeof description === 'object' ? (description.cs || '') : description,
    description_en: typeof description === 'object' ? (description.en || '') : '',
    description_de: typeof description === 'object' ? (description.de || '') : '',
    description_sk: typeof description === 'object' ? (description.sk || '') : '',
    price: legacyProduct.price || 0,
    currency: legacyProduct.currency || 'CZK',
    category: legacyProduct.category || 'unlimited',
    image: legacyProduct.image || '/ct_logo_light.svg',
    images: legacyProduct.images ? JSON.stringify(legacyProduct.images) : '',
    manual: legacyProduct.manual || '',
    video_tutorial: legacyProduct.videoTutorial || '',
    in_stock: legacyProduct.inStock ?? true,
    featured: legacyProduct.featured ?? false,
    discount: legacyProduct.discount || 0,
    is_on_sale: legacyProduct.isOnSale ?? false,
    is_sold_out: legacyProduct.isSoldOut ?? false,
    is_recommended: legacyProduct.isRecommended ?? false,
    is_rental: legacyProduct.isRental ?? false,
    rental_price_monthly: legacyProduct.rentalPriceMonthly ?? 0,
    rental_insurance_monthly: legacyProduct.rentalInsuranceMonthly ?? 0,
    rental_hourly_rate: legacyProduct.rentalHourlyRate ?? 0,
    vat_included: legacyProduct.vatIncluded ?? false,
    order_index: legacyProduct.order || 0
  };
};

// Utility function to convert Supabase product to legacy format
export const convertToLegacyProduct = (supabaseProduct: any): any => {
  return {
    id: supabaseProduct.id,
    name: {
      cs: supabaseProduct.name_cs,
      en: supabaseProduct.name_en || '',
      de: supabaseProduct.name_de || '',
      sk: supabaseProduct.name_sk || ''
    },
    description: {
      cs: supabaseProduct.description_cs || '',
      en: supabaseProduct.description_en || '',
      de: supabaseProduct.description_de || '',
      sk: supabaseProduct.description_sk || ''
    },
    price: supabaseProduct.price,
    currency: supabaseProduct.currency,
    category: supabaseProduct.category,
    image: supabaseProduct.image,
    images: supabaseProduct.images ? JSON.parse(supabaseProduct.images) : [],
    manual: supabaseProduct.manual || '',
    videoTutorial: supabaseProduct.video_tutorial || '',
    inStock: supabaseProduct.in_stock,
    featured: supabaseProduct.featured,
    discount: supabaseProduct.discount || 0,
    isOnSale: supabaseProduct.is_on_sale || false,
    isSoldOut: supabaseProduct.is_sold_out || false,
    isRecommended: supabaseProduct.is_recommended || false,
    isRental: supabaseProduct.is_rental || false,
    rentalPriceMonthly: supabaseProduct.rental_price_monthly || 0,
    rentalInsuranceMonthly: supabaseProduct.rental_insurance_monthly || 0,
    rentalHourlyRate: supabaseProduct.rental_hourly_rate || 0,
    vatIncluded: supabaseProduct.vat_included || false,
    order: supabaseProduct.order_index || 0,
    categories: supabaseProduct.category_ids || []
  };
};

// News functions
export const getNews = async () => {
  try {
    const { data, error } = await supabase
      .from('news')
      .select('*')
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching news:', error);
      throw new Error(`Failed to fetch news: ${error.message}`);
    }

    return data || [];
  } catch (err: any) {
    console.error('Exception fetching news:', err);
    throw new Error(`Failed to fetch news: ${err.message || 'Unknown error'}`);
  }
};

export const createNews = async (newsData: Omit<News, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('news')
    .insert([newsData])
    .select()
    .single();

  if (error) {
    console.error('Error creating news:', error);
    throw error;
  }

  return data;
};

export const updateNews = async (id: string, newsData: Partial<News>) => {
  const { data, error } = await supabase
    .from('news')
    .update({ ...newsData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating news:', error);
    throw error;
  }

  return data;
};

export const deleteNews = async (id: string) => {
  const { error } = await supabase
    .from('news')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting news:', error);
    throw error;
  }
};

export const convertLegacyNews = (legacyNews: any): Omit<News, 'id' | 'created_at' | 'updated_at'> => {
  const getField = (field: any) => {
    if (typeof field === 'object' && field !== null) {
      return field;
    }
    return typeof field === 'string' ? field : '';
  };

  const title = getField(legacyNews.title);
  const excerpt = getField(legacyNews.excerpt);
  const content = getField(legacyNews.content);

  return {
    title_cs: typeof title === 'object' ? (title.cs || '') : (title || ''),
    title_en: typeof title === 'object' ? (title.en || '') : '',
    title_de: typeof title === 'object' ? (title.de || '') : '',
    title_sk: typeof title === 'object' ? (title.sk || '') : '',
    excerpt_cs: typeof excerpt === 'object' ? (excerpt.cs || '') : (excerpt || ''),
    excerpt_en: typeof excerpt === 'object' ? (excerpt.en || '') : '',
    excerpt_de: typeof excerpt === 'object' ? (excerpt.de || '') : '',
    excerpt_sk: typeof excerpt === 'object' ? (excerpt.sk || '') : '',
    content_cs: typeof content === 'object' ? (content.cs || '') : (content || ''),
    content_en: typeof content === 'object' ? (content.en || '') : '',
    content_de: typeof content === 'object' ? (content.de || '') : '',
    content_sk: typeof content === 'object' ? (content.sk || '') : '',
    date: legacyNews.date || new Date().toISOString().split('T')[0],
    image: legacyNews.image || '/-ct_logo_light copy copy.png',
    category: legacyNews.category || 'company-news'
  };
};

export const convertToLegacyNews = (supabaseNews: News): any => {
  return {
    id: supabaseNews.id,
    title: {
      cs: supabaseNews.title_cs || '',
      en: supabaseNews.title_en || '',
      de: supabaseNews.title_de || '',
      sk: supabaseNews.title_sk || ''
    },
    excerpt: {
      cs: supabaseNews.excerpt_cs || '',
      en: supabaseNews.excerpt_en || '',
      de: supabaseNews.excerpt_de || '',
      sk: supabaseNews.excerpt_sk || ''
    },
    content: {
      cs: supabaseNews.content_cs || '',
      en: supabaseNews.content_en || '',
      de: supabaseNews.content_de || '',
      sk: supabaseNews.content_sk || ''
    },
    date: supabaseNews.date,
    image: supabaseNews.image || '/-ct_logo_light copy copy.png',
    category: supabaseNews.category || 'company-news'
  };
};

// Reference functions
export const getReferences = async (category?: string) => {
  try {
    let query = supabase
      .from('project_references')
      .select('*')
      .order('order_index', { ascending: true });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching references:', error);
      throw new Error(`Failed to fetch references: ${error.message}`);
    }

    return data || [];
  } catch (err: any) {
    console.error('Exception fetching references:', err);
    throw new Error(`Failed to fetch references: ${err.message || 'Unknown error'}`);
  }
};

export const createReference = async (referenceData: Omit<ProjectReference, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('project_references')
    .insert([referenceData])
    .select()
    .single();

  if (error) {
    console.error('Error creating reference:', error);
    throw error;
  }

  return data;
};

export const updateReference = async (id: string, referenceData: Partial<ProjectReference>) => {
  const { data, error } = await supabase
    .from('project_references')
    .update({ ...referenceData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating reference:', error);
    throw error;
  }

  return data;
};

export const deleteReference = async (id: string) => {
  const { error } = await supabase
    .from('project_references')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting reference:', error);
    throw error;
  }
};

// Research article type
export interface ResearchArticle {
  id: string;
  title_cz: string;
  title_en?: string;
  title_de?: string;
  title_sk?: string;
  description_cz: string;
  description_en?: string;
  description_de?: string;
  description_sk?: string;
  image_url?: string;
  images?: string[];
  video_url?: string;
  date?: string;
  category?: string;
  order_index: number;
  created_at?: string;
  updated_at?: string;
}

// Research functions
export const getResearchArticles = async () => {
  try {
    const { data, error } = await supabase
      .from('unlimited_research')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching research articles:', error);
      throw new Error(`Failed to fetch research articles: ${error.message}`);
    }

    return data || [];
  } catch (err: any) {
    console.error('Exception fetching research articles:', err);
    throw new Error(`Failed to fetch research articles: ${err.message || 'Unknown error'}`);
  }
};

export const createResearchArticle = async (articleData: Omit<ResearchArticle, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('unlimited_research')
    .insert([articleData])
    .select()
    .single();

  if (error) {
    console.error('Error creating research article:', error);
    throw error;
  }

  return data;
};

export const updateResearchArticle = async (id: string, articleData: Partial<ResearchArticle>) => {
  const { data, error } = await supabase
    .from('unlimited_research')
    .update({ ...articleData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating research article:', error);
    throw error;
  }

  return data;
};

export const deleteResearchArticle = async (id: string) => {
  const { error } = await supabase
    .from('unlimited_research')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting research article:', error);
    throw error;
  }
};

// Prototypes functions
export const getPrototypes = async () => {
  try {
    const { data, error } = await supabase
      .from('unlimited_prototypes')
      .select('*')
      .order('order_index', { ascending: true });
    if (error) throw new Error(`Failed to fetch prototypes: ${error.message}`);
    return data || [];
  } catch (err: any) {
    throw new Error(`Failed to fetch prototypes: ${err.message || 'Unknown error'}`);
  }
};

export const createPrototype = async (articleData: Omit<ResearchArticle, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('unlimited_prototypes')
    .insert([articleData])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updatePrototype = async (id: string, articleData: Partial<ResearchArticle>) => {
  const { data, error } = await supabase
    .from('unlimited_prototypes')
    .update({ ...articleData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deletePrototype = async (id: string) => {
  const { error } = await supabase
    .from('unlimited_prototypes')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

// Experimental functions
export const getExperimental = async () => {
  try {
    const { data, error } = await supabase
      .from('unlimited_experimental')
      .select('*')
      .order('order_index', { ascending: true });
    if (error) throw new Error(`Failed to fetch experimental: ${error.message}`);
    return data || [];
  } catch (err: any) {
    throw new Error(`Failed to fetch experimental: ${err.message || 'Unknown error'}`);
  }
};

export const createExperimental = async (articleData: Omit<ResearchArticle, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('unlimited_experimental')
    .insert([articleData])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updateExperimental = async (id: string, articleData: Partial<ResearchArticle>) => {
  const { data, error } = await supabase
    .from('unlimited_experimental')
    .update({ ...articleData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteExperimental = async (id: string) => {
  const { error } = await supabase
    .from('unlimited_experimental')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

// ─── Leasing categories ──────────────────────────────────────────────
export interface LeasingCategory {
  id: string;
  name: string;
  color: string;
  order_index: number;
}

export const getLeasingCategories = async (): Promise<LeasingCategory[]> => {
  const { data, error } = await supabase
    .from('leasing_categories')
    .select('*')
    .order('order_index', { ascending: true });
  if (error) throw new Error(`Failed to fetch leasing categories: ${error.message}`);
  return (data || []) as LeasingCategory[];
};

export const createLeasingCategory = async (name: string, color: string): Promise<LeasingCategory> => {
  const { data, error } = await supabase
    .from('leasing_categories')
    .insert([{ name, color, order_index: 0 }])
    .select()
    .single();
  if (error) throw error;
  return data as LeasingCategory;
};

export const deleteLeasingCategory = async (id: string) => {
  const { error } = await supabase
    .from('leasing_categories')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

// ─── Product ↔ leasing-category assignments ──────────────────────────
export const getProductLeasingAssignments = async (): Promise<Record<string, string[]>> => {
  const { data, error } = await supabase
    .from('product_leasing_categories')
    .select('product_id, leasing_category_id');
  if (error) throw new Error(`Failed to fetch leasing assignments: ${error.message}`);
  const map: Record<string, string[]> = {};
  (data || []).forEach((r: any) => {
    if (!map[r.product_id]) map[r.product_id] = [];
    map[r.product_id].push(r.leasing_category_id);
  });
  return map;
};

export const assignProductToLeasingCategory = async (productId: string, leasingCategoryId: string) => {
  const { error } = await supabase
    .from('product_leasing_categories')
    .insert([{ product_id: productId, leasing_category_id: leasingCategoryId }]);
  if (error && error.code !== '23505') throw error; // 23505 = already exists
};

export const unassignProductFromLeasingCategory = async (productId: string, leasingCategoryId: string) => {
  const { error } = await supabase
    .from('product_leasing_categories')
    .delete()
    .eq('product_id', productId)
    .eq('leasing_category_id', leasingCategoryId);
  if (error) throw error;
};

export const assignProductsToLeasingCategory = async (productIds: string[], leasingCategoryId: string) => {
  const rows = productIds.map(pid => ({ product_id: pid, leasing_category_id: leasingCategoryId }));
  if (rows.length === 0) return;
  const { error } = await supabase
    .from('product_leasing_categories')
    .insert(rows);
  if (error && error.code !== '23505') throw error;
};

// ─── Leasing requests ────────────────────────────────────────────────
export interface LeasingRequest {
  product_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  company_name?: string;
  billing_address?: string;
  notes?: string;
}

export const createLeasingRequest = async (req: LeasingRequest) => {
  const { data, error } = await supabase
    .from('leasing_requests')
    .insert([req])
    .select()
    .single();
  if (error) throw error;
  return data;
};